import { createEffect, createMemo, on, Accessor } from 'solid-js';
import { CapabilityRegistry } from '../capability-registry/capability-registry';
import { CapabilityRegistryContainer } from '../capability-registry/capability-registry-container';
import { FitToViewBaseCapabilityCoordinator } from './fit-to-view-base-capability-coordinator';
import { LoadableItem, LoadState } from '@trimble/data-loading';

export class FitToViewOnloadCapabilityCoordinator extends FitToViewBaseCapabilityCoordinator {
  private readonly paneNeedsFit: Map<CapabilityRegistryContainer, boolean> =
    new Map();

  constructor(
    registry: CapabilityRegistry,
    dataLoadingService: { items: Accessor<LoadableItem[]> }
  ) {
    super(registry);

    // Initialize all existing panes to need fit
    registry.paneContainers().forEach((pane) => {
      this.paneNeedsFit.set(pane, true);
    });

    const loadStates = createMemo(() =>
      dataLoadingService.items().map((i) => i.loadState)
    );

    // Track when all items are unloaded then reset all panes to need fit
    createEffect(
      on(loadStates, () => {
        const loadItems = dataLoadingService
          .items()
          .filter(
            (i) =>
              i.loadState === LoadState.Loading ||
              i.loadState === LoadState.Reloading ||
              i.loadState === LoadState.LoadComplete
          );

        // When all items are unloaded, mark all panes as needing fit
        if (loadItems.length === 0) {
          registry.paneContainers().forEach((pane) => {
            this.paneNeedsFit.set(pane, true);
          });
        }
      })
    );

    // Perform fit-to-view on individual panes when items complete loading
    createEffect(
      on(loadStates, () => {
        const hasLoadedItems = dataLoadingService
          .items()
          .some((i) => i.loadState === LoadState.LoadComplete);

        if (hasLoadedItems) {
          void this.performFitToViewOnIndividualPanes();
        }
      })
    );

    // Track new panes being added to registry
    createEffect(
      on(
        () => registry.paneContainers().length,
        () => {
          // Mark any new panes as needing fit
          registry.paneContainers().forEach((pane) => {
            if (!this.paneNeedsFit.has(pane)) {
              this.paneNeedsFit.set(pane, true);
            }
          });
        }
      )
    );
  }

  private async performFitToViewOnIndividualPanes(
    modelId?: string
  ): Promise<void> {
    const promises = this.registry
      .paneContainers()
      .map((pane) => {
        // Skip if this pane doesn't need fit
        if (!this.paneNeedsFit.get(pane)) {
          return undefined;
        }

        if (!this.isPaneEligibleForFitToView(pane)) {
          return undefined;
        }

        // Mark this pane as no longer needing fit
        this.paneNeedsFit.set(pane, false);

        return modelId
          ? pane.viewerWrapper()?.viewer.camera.fitToView(modelId)
          : pane.viewerWrapper()?.viewer.camera.fitToView();
      })
      .filter((p): p is Promise<void> => p !== undefined);

    await Promise.all(promises);
  }

  /**
   * Suppresses fit-to-view for a specific pane.
   */
  public suppressFitToView(pane: CapabilityRegistryContainer): void {
    this.paneNeedsFit.set(pane, false);
  }

  public cleanup(): Promise<void> {
    this.paneNeedsFit.clear();
    return Promise.resolve();
  }
}
